import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { SortableTable } from '../ui/SortableTable';
import { FreshnessCell } from './FreshnessCell';
import type { ColumnDef, SortState } from '../ui/SortableTable';
import type { DossierProvinceRow } from '../../types/dossier';
import type { CrystalBallData, SpyOnMilitaryData, SurveyData, InfiltrateData } from '../../types/intel';
import { renderFuzzyValueDouble } from '../../utils/fuzzyValue';
import { formatNumber, formatEnumLabel } from '../../utils/format';

interface MilitaryTabProps {
  provinces: DossierProvinceRow[];
  currentTick: number;
  sortState: SortState;
  onSort: (key: string) => void;
}

export function MilitaryTab({ provinces, currentTick, sortState, onSort }: MilitaryTabProps) {
  const navigate = useNavigate();

  const columns: ColumnDef<DossierProvinceRow>[] = [
    {
      key: 'provinceName',
      header: 'Province',
      sticky: true,
      sortable: true,
      sortAccessor: (r) => r.provinceName,
      render: (r) => (
        <span>
          <span style={{ color: COLORS.text.primary, fontWeight: 500 }}>{r.provinceName}</span>
          <span style={{ color: COLORS.text.muted, marginLeft: 4, fontSize: 11 }}>({r.location})</span>
        </span>
      ),
    },
    {
      key: 'race',
      header: 'Race',
      sortable: true,
      sortAccessor: (r) => r.race,
      render: (r) => <span style={{ color: COLORS.text.tertiary }}>{formatEnumLabel(r.race)}</span>,
    },
    {
      key: 'land',
      header: 'Land',
      sortable: true,
      sortAccessor: (r) => r.land,
      render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(r.land)}</span>,
    },
    {
      key: 'nwpa',
      header: 'NW/Acre',
      sortable: true,
      sortAccessor: (r) => r.nwpa,
      render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(r.nwpa)}</span>,
    },
    {
      key: 'rawOffense',
      header: 'Raw Off.',
      sortable: true,
      sortAccessor: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        return som?.offensePoints ?? -1;
      },
      render: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        if (!som?.offensePoints) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(Math.round(som.offensePoints))}</span>;
      },
    },
    {
      key: 'ome',
      header: 'OME',
      sortable: true,
      sortAccessor: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        return som?.offensiveME ?? -1;
      },
      render: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        if (som?.offensiveME == null) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{(som.offensiveME * 100).toFixed(1)}%</span>;
      },
    },
    {
      key: 'modifiedOffense',
      header: 'Mod. Off.',
      sortable: true,
      sortAccessor: (r) => {
        if (!r.modifiedOffense) return -1;
        return Math.round((r.modifiedOffense.low + r.modifiedOffense.high) / 2);
      },
      render: (r) => {
        if (!r.modifiedOffense) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return (
          <span style={{ color: COLORS.text.primary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {renderFuzzyValueDouble(r.modifiedOffense)}
          </span>
        );
      },
    },
    {
      key: 'generals',
      header: 'Generals',
      sortable: true,
      sortAccessor: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        return som?.generals != null ? som.generals : -1;
      },
      render: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        if (som?.generals == null) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(som.generals)}</span>;
      },
    },
    {
      key: 'warHorses',
      header: 'War Horses',
      sortable: true,
      sortAccessor: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb?.warHorses ?? -1;
      },
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        if (!cb) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatNumber(cb.warHorses)}
            <span style={{ color: COLORS.text.muted, fontSize: 10, marginLeft: 3 }}>(in raw off)</span>
          </span>
        );
      },
    },
    {
      key: 'prisoners',
      header: 'Prisoners',
      sortable: true,
      sortAccessor: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        return cb?.prisoners ?? -1;
      },
      render: (r) => {
        const cb = r.latestCb?.data as CrystalBallData | null;
        if (!cb) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatNumber(cb.prisoners)}
            <span style={{ color: COLORS.text.muted, fontSize: 10, marginLeft: 3 }}>(in raw off ×3)</span>
          </span>
        );
      },
    },
    {
      key: 'rawDefense',
      header: 'Raw Def.',
      sortable: true,
      sortAccessor: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        return som?.defensePoints ?? -1;
      },
      render: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        if (!som?.defensePoints) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(Math.round(som.defensePoints))}</span>;
      },
    },
    {
      key: 'dme',
      header: 'DME',
      sortable: true,
      sortAccessor: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        return som?.defensiveME != null ? som.defensiveME : -1;
      },
      render: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        if (som?.defensiveME == null) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{(som.defensiveME * 100).toFixed(1)}%</span>;
      },
    },
    {
      key: 'modifiedDefense',
      header: 'Mod. Def.',
      sortable: true,
      sortAccessor: (r) => {
        if (!r.modifiedDefense) return -1;
        return Math.round((r.modifiedDefense.low + r.modifiedDefense.high) / 2);
      },
      render: (r) => {
        if (!r.modifiedDefense) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return (
          <span style={{ color: COLORS.negative, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {renderFuzzyValueDouble(r.modifiedDefense)}
          </span>
        );
      },
    },
    {
      key: 'armyStatus',
      header: 'Army Status',
      sortable: false,
      render: (r) => {
        const som = r.latestSom?.data as SpyOnMilitaryData | null;
        if (!som) return <span style={{ color: COLORS.text.muted }}>—</span>;
        if (!som.hasArmiesOut) return <span style={{ color: COLORS.positive }}>HOME</span>;
        const ret = som.nextReturnTicks;
        const retStr = ret != null ? `${ret}t` : '?';
        return <span style={{ color: COLORS.warning }}>OUT (ret. in {retStr})</span>;
      },
    },
    {
      key: 'rawTPA',
      header: 'TPA',
      sortable: true,
      sortAccessor: (r) => {
        const inf = r.latestInfiltrate?.data as InfiltrateData | null;
        return inf?.rawTPA != null ? inf.rawTPA : -1;
      },
      render: (r) => {
        const inf = r.latestInfiltrate?.data as InfiltrateData | null;
        if (inf?.rawTPA == null) return <span style={{ color: COLORS.text.muted }}>—</span>;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{inf.rawTPA.toFixed(2)}</span>;
      },
    },
    {
      key: 'watchTowers',
      header: 'Watch Towers',
      sortable: true,
      sortAccessor: (r) => {
        const survey = r.latestSurvey?.data as SurveyData | null;
        return survey?.buildings?.['WATCH_TOWER'] ?? -1;
      },
      render: (r) => {
        const survey = r.latestSurvey?.data as SurveyData | null;
        const wt = survey?.buildings?.['WATCH_TOWER'];
        return wt != null
          ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(wt)}</span>
          : <span style={{ color: COLORS.text.muted }}>—</span>;
      },
    },
    {
      key: 'guardStations',
      header: 'Guard Stations',
      sortable: true,
      sortAccessor: (r) => {
        const survey = r.latestSurvey?.data as SurveyData | null;
        return survey?.buildings?.['GUARD_STATION'] ?? -1;
      },
      render: (r) => {
        const survey = r.latestSurvey?.data as SurveyData | null;
        const gs = survey?.buildings?.['GUARD_STATION'];
        return gs != null
          ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(gs)}</span>
          : <span style={{ color: COLORS.text.muted }}>—</span>;
      },
    },
    {
      key: 'freshness',
      header: 'Freshness',
      sortable: true,
      sortAccessor: (r) => {
        const ticks = [
          r.latestSom?.capturedAtTick ?? 0,
          r.latestCb?.capturedAtTick ?? 0,
          r.latestSurvey?.capturedAtTick ?? 0,
          r.latestInfiltrate?.capturedAtTick ?? 0,
        ];
        return Math.max(...ticks);
      },
      render: (r) => {
        const ticks = [
          r.latestSom?.capturedAtTick ?? 0,
          r.latestCb?.capturedAtTick ?? 0,
          r.latestSurvey?.capturedAtTick ?? 0,
          r.latestInfiltrate?.capturedAtTick ?? 0,
        ].filter((t) => t > 0);
        const bestTick = ticks.length > 0 ? Math.max(...ticks) : null;
        return <FreshnessCell capturedAtTick={bestTick} currentTick={currentTick} />;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => {
        const hasSom = r.latestSom !== null;
        const hasCb = r.latestCb !== null;
        const hasSurvey = r.latestSurvey !== null;
        if (hasSom && hasCb && hasSurvey) return null;
        const params = new URLSearchParams({
          targetId: r.provinceId,
          targetName: r.provinceName,
          targetLoc: r.location,
        });
        const label = !hasSom ? 'Gather SoM' : !hasCb ? 'Gather CB' : 'Gather Survey';
        return (
          <button
            onClick={() => navigate(`/thievery?${params.toString()}`)}
            style={{
              fontSize: 11,
              padding: '2px 8px',
              background: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text.tertiary,
              cursor: 'pointer',
              borderRadius: 2,
            }}
          >
            {label}
          </button>
        );
      },
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={provinces}
      getRowKey={(r) => r.provinceId}
      sortState={sortState}
      onSort={onSort}
    />
  );
}
